// Copyright (c) Ping. All rights reserved.
//
// Device + user-identity Ed25519 keypairs used by /v1/auth/verify on
// openmls.ping.live.
//
// Wire contract (see `services/api/internal/auth/device.go`):
//
//   device_id            = SHA-256(device_signing_pubkey)
//   binding_msg          = "ping/device-binding/v1" ‖ user_identity_pubkey ‖ device_id
//   device_binding_sig   = Ed25519.Sign(user_identity_private_key, binding_msg)
//
// All pubkeys + signatures travel as base64url (no padding). The
// private keys never leave the browser — they're stored as
// non-exportable Web Crypto `CryptoKey` instances in IndexedDB.

import { db } from '@core/persistence/db';

const DEVICE_KEY_STORAGE_KEY = '__device_signing_key';
const USER_KEY_STORAGE_KEY = '__user_identity_key';
const DEVICE_BINDING_PREFIX = 'ping/device-binding/v1';

interface StoredKeyPair {
  readonly publicKeyBytes: number[];
  readonly privateKey: CryptoKey;
}

// Single-flight cache: every concurrent caller of `loadOrCreateKeyPair`
// for a given storage key shares the same in-flight promise so we never
// generate-then-overwrite different keypairs in parallel. Critical for
// the first verify call after install — without it, the authClient's
// `Promise.all([getDevicePubkey, getUserPubkey, signDeviceBinding, ...])`
// would each independently mint fresh keys, and the signature wouldn't
// match what the server reconstructs from `SHA-256(device_signing_pubkey)`.
const keyPairCache = new Map<string, Promise<StoredKeyPair>>();

function loadOrCreateKeyPair(storageKey: string): Promise<StoredKeyPair> {
  let inflight = keyPairCache.get(storageKey);
  if (inflight) return inflight;
  inflight = loadOrCreateKeyPairImpl(storageKey).catch((err) => {
    // Failed loads must not poison the cache; drop the entry so the
    // next caller retries.
    keyPairCache.delete(storageKey);
    throw err;
  });
  keyPairCache.set(storageKey, inflight);
  return inflight;
}

async function loadOrCreateKeyPairImpl(storageKey: string): Promise<StoredKeyPair> {
  const existing = await db.settings_kv.get(storageKey);
  if (existing?.value) {
    const stored = existing.value as Partial<StoredKeyPair>;
    if (
      stored.privateKey instanceof CryptoKey &&
      Array.isArray(stored.publicKeyBytes) &&
      stored.publicKeyBytes.length === 32
    ) {
      return {
        publicKeyBytes: stored.publicKeyBytes,
        privateKey: stored.privateKey,
      };
    }
  }

  const keyPair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;

  const publicKeyBuffer = await crypto.subtle.exportKey('raw', keyPair.publicKey);
  const publicKeyBytes = Array.from(new Uint8Array(publicKeyBuffer));
  const fresh: StoredKeyPair = {
    publicKeyBytes,
    privateKey: keyPair.privateKey,
  };

  await db.settings_kv.put({
    key: storageKey,
    value: fresh,
    updatedAt: Date.now(),
  });

  return fresh;
}

export async function getDeviceSigningPubkeyBytes(): Promise<Uint8Array> {
  const { publicKeyBytes } = await loadOrCreateKeyPair(DEVICE_KEY_STORAGE_KEY);
  return new Uint8Array(publicKeyBytes);
}

export async function getUserIdentityPubkeyBytes(): Promise<Uint8Array> {
  const { publicKeyBytes } = await loadOrCreateKeyPair(USER_KEY_STORAGE_KEY);
  return new Uint8Array(publicKeyBytes);
}

/**
 * Export the user-identity keypair as a JWK bundle suitable for the
 * recovery backup. Returned shape is `{ jwk: JsonWebKey,
 * publicKeyBytes: number[] }` — the JWK carries both `x` (public)
 * and `d` (private) for re-import, the raw pubkey bytes are kept
 * verbatim so `restoreUserIdentityKeyFromJwk` writes a row whose
 * shape matches the local `loadOrCreateKeyPair` reader on disk.
 *
 * `crypto.subtle.exportKey('jwk', privateKey)` is the canonical
 * round-trip — RFC 8037 Ed25519 JWKs are interoperable with the
 * WebCrypto importKey path used on restore.
 */
export async function exportUserIdentityForBackup(): Promise<{
  jwk: JsonWebKey;
  publicKeyBytes: number[];
}> {
  const stored = await loadOrCreateKeyPair(USER_KEY_STORAGE_KEY);
  const jwk = (await crypto.subtle.exportKey('jwk', stored.privateKey)) as JsonWebKey;
  return { jwk, publicKeyBytes: stored.publicKeyBytes };
}

/**
 * Restore the user-identity keypair from a previously-exported JWK
 * bundle. Writes a row to `settings_kv` under the same key
 * `loadOrCreateKeyPair` reads from, so subsequent calls to
 * `getUserIdentityPubkeyBytes` / `signDeviceBinding` use the
 * recovered key — same pubkey the BE pinned at the original signup.
 *
 * Invalidates the in-memory single-flight cache so the next read
 * goes through the disk row we just wrote (otherwise the cached
 * pre-restore keypair would shadow it).
 */
export async function restoreUserIdentityFromBackup(bundle: {
  jwk: JsonWebKey;
  publicKeyBytes: number[];
}): Promise<void> {
  const privateKey = await crypto.subtle.importKey(
    'jwk',
    bundle.jwk,
    { name: 'Ed25519' },
    true,
    ['sign'],
  );
  await db.settings_kv.put({
    key: USER_KEY_STORAGE_KEY,
    value: {
      publicKeyBytes: bundle.publicKeyBytes,
      privateKey,
    } satisfies StoredKeyPair,
    updatedAt: Date.now(),
  });
  // Drop the cached promise so the next caller re-reads from disk.
  keyPairCache.delete(USER_KEY_STORAGE_KEY);
}

/**
 * Export the device signing private key as raw 32 Ed25519 secret
 * bytes. The CryptoKey is created with `extractable: true`, so we
 * round-trip through JWK to get the `d` field (base64url of the raw
 * scalar) — `crypto.subtle.exportKey('raw', ...)` doesn't support
 * private keys on Ed25519.
 *
 * Used to seed the MLS SDK's device signing key via
 * `ClientConfig.deviceSigningSecretKey` on first init, so the SDK's
 * `device_id` (which it stamps into every envelope's `sender_device`
 * field) matches `SHA-256(device_signing_pubkey)` — the same value
 * the BE assigns to the JWT's `device_id` claim. Without this
 * alignment, the message-service rejects every send with
 * `sender_device_mismatch`.
 */
export async function getDeviceSigningSecretBytes(): Promise<Uint8Array> {
  const { privateKey } = await loadOrCreateKeyPair(DEVICE_KEY_STORAGE_KEY);
  const jwk = (await crypto.subtle.exportKey('jwk', privateKey)) as {
    d?: string;
  };
  if (!jwk.d) {
    throw new Error('device signing key is not extractable (missing JWK `d`)');
  }
  return base64urlToBytes(jwk.d);
}

function base64urlToBytes(s: string): Uint8Array {
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const standard = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(standard);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Compute the device id the server will derive — SHA-256 of the device
 * signing pubkey. The server never echoes the device id, so callers
 * that need it for local persistence must derive it the same way.
 */
export async function getDeviceIdBytes(): Promise<Uint8Array> {
  const pubkey = await getDeviceSigningPubkeyBytes();
  const hash = await crypto.subtle.digest('SHA-256', pubkey as BufferSource);
  return new Uint8Array(hash);
}

/**
 * Produce the binding signature the verify endpoint demands. Signs
 * `"ping/device-binding/v1" || user_identity_pubkey || device_id`
 * with the user identity private key (Ed25519).
 *
 * `overrideDeviceId` lets callers sign over a client-supplied
 * device_id (e.g. an MLS SDK's `ownDeviceId`) instead of the legacy
 * `SHA256(device_signing_pubkey)` derivation. The same value MUST be
 * sent as `device_id` in the verify request — BE
 * `parseDeviceCredentials` verifies the signature against whichever
 * device_id it uses.
 */
export async function signDeviceBinding(opts?: {
  readonly overrideDeviceId?: Uint8Array;
}): Promise<Uint8Array> {
  const userPair = await loadOrCreateKeyPair(USER_KEY_STORAGE_KEY);
  const userPubkey = new Uint8Array(userPair.publicKeyBytes);

  let deviceId: Uint8Array;
  if (opts?.overrideDeviceId) {
    deviceId = opts.overrideDeviceId;
  } else {
    const devicePair = await loadOrCreateKeyPair(DEVICE_KEY_STORAGE_KEY);
    const devicePubkey = new Uint8Array(devicePair.publicKeyBytes);
    const deviceIdBuffer = await crypto.subtle.digest(
      'SHA-256',
      devicePubkey as BufferSource,
    );
    deviceId = new Uint8Array(deviceIdBuffer);
  }

  const prefix = new TextEncoder().encode(DEVICE_BINDING_PREFIX);
  const msg = new Uint8Array(prefix.length + userPubkey.length + deviceId.length);
  msg.set(prefix, 0);
  msg.set(userPubkey, prefix.length);
  msg.set(deviceId, prefix.length + userPubkey.length);

  const sig = await crypto.subtle.sign(
    { name: 'Ed25519' },
    userPair.privateKey,
    msg as BufferSource,
  );
  return new Uint8Array(sig);
}

// ── Encoding helpers ────────────────────────────────────────────────

/** Base64url, no padding — matches the wire format the API expects. */
export function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    if (b === undefined) continue;
    bin += String.fromCharCode(b);
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Legacy helper kept for compat — some non-auth callers still use hex. */
export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}

// ── Back-compat aliases for older imports ──────────────────────────

/** @deprecated Use `getDeviceSigningPubkeyBytes` (same return). */
export const getDevicePublicKeyBytes = getDeviceSigningPubkeyBytes;

/** @deprecated Use `getDeviceIdBytes` (returns 32B SHA-256, base64url-able). */
export async function getOrCreateDeviceId(): Promise<string> {
  return bytesToBase64Url(await getDeviceIdBytes());
}
